import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { getAdminLogs } from '../api';
import { toast } from 'sonner';
import { Search } from 'lucide-react';

export const AdminLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 30;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (actionFilter) params.action = actionFilter;
      const res = await getAdminLogs(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Erreur chargement logs');
    } finally {
      setLoading(false);
    }
  }, [actionFilter, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / limit);

  const actionColor = (action) => {
    if (action?.includes('delete') || action?.includes('reject')) return 'text-red-400';
    if (action?.includes('approve') || action?.includes('create')) return 'text-green-400';
    if (action?.includes('update') || action?.includes('toggle')) return 'text-blue-400';
    return 'text-muted-foreground';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Journal d'activité</h1>
          <p className="text-muted-foreground text-sm">{total} entrées au total</p>
        </div>

        {/* Filtre */}
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              className="pl-9 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Filtrer par action..."
              value={actionFilter}
              onChange={e => { setActionFilter(e.target.value); setPage(1); }}
            />
          </div>
          <button onClick={fetchLogs} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors">
            Filtrer
          </button>
        </div>

        {/* Logs */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground">Chargement...</div>
          ) : logs.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">Aucun log trouvé</div>
          ) : (
            <div className="divide-y divide-border">
              {logs.map(log => (
                <div key={log.id} className="px-5 py-3 hover:bg-muted/20 transition-colors flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-medium font-mono ${actionColor(log.action)}`}>{log.action}</span>
                      {log.target_type && (
                        <span className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">{log.target_type}</span>
                      )}
                    </div>
                    {log.details && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-lg">{log.details}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm text-foreground">{log.admin_username || `Admin #${log.admin_id}`}</div>
                    <div className="text-xs text-muted-foreground">
                      {log.created_at ? new Date(log.created_at).toLocaleString('fr-FR') : '-'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 text-sm bg-card border border-border rounded-lg disabled:opacity-40 hover:bg-accent transition-colors">Précédent</button>
            <span className="text-sm text-muted-foreground">Page {page}/{totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 text-sm bg-card border border-border rounded-lg disabled:opacity-40 hover:bg-accent transition-colors">Suivant</button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
